const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'summary',
  '[tabindex]:not([tabindex="-1"])'
].join(',')

function visibleFocusable(element) {
  return element.offsetParent !== null || element === document.activeElement
}

function focusableChildren(element) {
  return Array.from(element.querySelectorAll(FOCUSABLE_SELECTOR)).filter(visibleFocusable)
}

function installTrap(element, binding) {
  const options = binding.value || {}
  const previousFocus = document.activeElement
  const state = { onEscape: options.onEscape, previousFocus }

  element.__scribe11FocusTrapState = state

  const handler = (event) => {
    if (event.key === 'Escape' && typeof state.onEscape === 'function') {
      event.preventDefault()
      state.onEscape(event)
      return
    }
    if (event.key !== 'Tab') return

    const focusable = focusableChildren(element)
    if (focusable.length === 0) {
      event.preventDefault()
      element.focus()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  const focusInHandler = (event) => {
    if (!element.contains(event.target)) {
      const first = focusableChildren(element)[0] || element
      first.focus()
    }
  }

  element.__scribe11FocusTrap = handler
  element.__scribe11FocusIn = focusInHandler
  element.addEventListener('keydown', handler)
  document.addEventListener('focusin', focusInHandler)

  queueMicrotask(() => {
    if (!element.isConnected) return
    const first = focusableChildren(element)[0] || element
    first.focus()
  })
}

function removeTrap(element) {
  if (!element.__scribe11FocusTrap) return
  element.removeEventListener('keydown', element.__scribe11FocusTrap)
  document.removeEventListener('focusin', element.__scribe11FocusIn)
  const previousFocus = element.__scribe11FocusTrapState?.previousFocus
  if (previousFocus && previousFocus.isConnected && typeof previousFocus.focus === 'function') {
    queueMicrotask(() => previousFocus.focus())
  }
  delete element.__scribe11FocusTrap
  delete element.__scribe11FocusIn
  delete element.__scribe11FocusTrapState
}

export const focusTrap = {
  mounted: installTrap,
  updated(element, binding) {
    if (element.__scribe11FocusTrapState) {
      element.__scribe11FocusTrapState.onEscape = (binding.value || {}).onEscape
    }
  },
  beforeUnmount: removeTrap
}
